import {
  Box,
  Button,
  Container,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { debounce, get, isEmpty } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Components
import { useFormik } from 'formik';
import { Filter, Search } from 'react-feather';
import BreadcrumbWithBtnBack from 'src/components/BreadcrumbWithBtnBack';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import StyledPagination from 'src/components/common/StyledPagination';
import TabComponent from 'src/components/Tab/TabComponent';
import FormFilterBookmarks from 'src/features/Bookmark/Common/FormFilterBookmarks';
import CompanyBookmarkList from 'src/features/Bookmark/CompanyBookmarkList';
import CompareBookmarkList from 'src/features/Bookmark/CompareBookmarkList';
import { StyledWhiteBox, TABS_BOOKMARK } from 'src/features/Bookmark/config';
import PeopleBookmarkList from 'src/features/Bookmark/PeopleBookmarkList';
import TradeDataBookmarkList from 'src/features/Bookmark/TradeDataBookmarkList';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { BookmarkTypes } from 'src/model/bookmark';
import { useQueryGetBookmark } from 'src/services/queries/useQueryBookmark';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import * as Yup from 'yup';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
}));

const BookmarkPage = () => {
  const query = useQueryParams();
  const [, setSearchParams] = useSearchParams();
  const [openFormFilter, setOpenFormFilter] = useState(false);

  const [defaultSearchKey, setDefaultSearchKey] = useState(query?.search);
  const [selectedTab, setSelectedTab] = useState<BookmarkTypes>(BookmarkTypes.Company);

  useEffect(() => {
    setDefaultSearchKey(query?.search || '');
  }, [query?.search]);

  const onChangeSearch = useCallback(
    debounce(async (e) => {
      setSearchParams({ search: String(e.target.value), offset: '0', limit: LIMIT_GET_LIST.toString() });
    }, 1000),
    [query],
  );

  const getDefaultValue = (key: string) => {
    return get(query, key, '') as string;
  };

  const handleCloseFormFilter = () => {
    setOpenFormFilter(false);
  };

  const handleOpenFormFilter = () => {
    setOpenFormFilter(true);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      search: getDefaultValue('search'),
      date_from: getDefaultValue('date_from'),
      date_to: getDefaultValue('date_to'),
      limit: LIMIT_GET_LIST,
      offset: 0,
    },
    validationSchema: Yup.object().shape({
      date_from: Yup.string().max(255),
      date_to: Yup.string().max(255),
    }),
    onSubmit: async (values) => {
      handleCloseFormFilter();
      const paramFilter = handleQueryParamFromObject(values);
      setSearchParams(paramFilter);
    },
  });

  const { data, isFetching } = useQueryGetBookmark(selectedTab, {
    limit: LIMIT_GET_LIST,
    offset: query?.offset || 0,
    ...query,
  });

  const title = useMemo(() => {
    switch (selectedTab) {
      case BookmarkTypes.Company:
        return 'Company Profile Bookmark';
      case BookmarkTypes.People:
        return 'People Profile Bookmark';
      case BookmarkTypes.TradeData:
        return 'Trade Data Bookmark';
      case BookmarkTypes.CompareCompany:
        return 'Compare Company Bookmark';
      default:
        return '';
    }
  }, [selectedTab]);

  const placeHolderSearch = useMemo(() => {
    switch (selectedTab) {
      case BookmarkTypes.Company:
        return 'Search Company Bookmark';
      case BookmarkTypes.People:
        return 'Search name, or company';
      case BookmarkTypes.TradeData:
        return 'Search with HS Code or Product Name';
      case BookmarkTypes.CompareCompany:
        return 'Search company';
      default:
        return '';
    }
  }, [selectedTab]);

  const renderListBookmark = () => {
    switch (selectedTab) {
      case BookmarkTypes.Company:
        return <CompanyBookmarkList listBookmark={getArrayDefault(data?.data)} />;
      case BookmarkTypes.People:
        return <PeopleBookmarkList listBookmark={getArrayDefault(data?.data)} />;
      case BookmarkTypes.TradeData:
        return <TradeDataBookmarkList listBookmark={getArrayDefault(data?.data)} />;
      case BookmarkTypes.CompareCompany:
        return <CompareBookmarkList listBookmark={getArrayDefault(data?.data)} />;
      default:
        return <CompanyBookmarkList listBookmark={getArrayDefault(data?.data)} />;
    }
  };

  return (
    <StyledContainer maxWidth={'xl'}>
      <BreadcrumbWithBtnBack
        listItem={[
          {
            text: 'Home',
          },
          {
            text: 'Bookmark Page',
          },
        ]}
        showButtonBack={false}
      />
      <Box mt={2} mb={3}>
        <TabComponent
          selected={selectedTab}
          handleChange={(_, e: string) => {
            setSearchParams((searchParams) => {
              searchParams.set('search', '');
              searchParams.set('offset', '0');
              searchParams.set('limit', LIMIT_GET_LIST.toString());
              return searchParams;
            });
            setSelectedTab(e as BookmarkTypes);
          }}
          tabs={TABS_BOOKMARK}
        />
      </Box>
      <StyledWhiteBox>
        <SectionErrorBoundary resetKey="ListBookmarkResults" sectionName="ListBookmarkResults">
          <Typography variant="h6">{title}</Typography>
          <FormikForm formik={formik}>
            <Stack direction="row" spacing={2} mt={2} mb={3}>
              <Box display="flex" flex={1}>
                <TextField
                  sx={{ flex: 1 }}
                  id="search"
                  name="search"
                  placeholder={placeHolderSearch}
                  size="small"
                  onChange={(e) => {
                    onChangeSearch(e);
                    setDefaultSearchKey(e.target.value);
                  }}
                  value={defaultSearchKey}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconFeather icon={<Search />} muiProps={{ color: 'disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box display="flex">
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<IconFeather icon={<Filter />} />}
                  onClick={handleOpenFormFilter}
                >
                  Filter
                </Button>
              </Box>
            </Stack>
            {isFetching ? <SkeletonProfile /> : renderListBookmark()}
            <FormFilterBookmarks open={openFormFilter} handleClose={handleCloseFormFilter} />
          </FormikForm>
          {!isEmpty(data?.data) && (
            <Stack mt={2} alignItems="center">
              <StyledPagination
                total_pages={data?.total_pages || 0}
                page={Number(query.offset || 0) + 1}
                onChange={function (_: ChangeEvent<unknown>, value: number): void {
                  setSearchParams((searchParams) => {
                    searchParams.set('offset', String(value - 1));
                    return searchParams;
                  });
                }}
              />
            </Stack>
          )}
        </SectionErrorBoundary>
      </StyledWhiteBox>
    </StyledContainer>
  );
};

export default BookmarkPage;

const SkeletonProfile = () => {
  return (
    <Grid container spacing={2}>
      <Grid xs={12} md={6} lg={6} xl={4} item>
        <Skeleton animation="wave" variant="rectangular" height={200} />
      </Grid>
      <Grid xs={12} md={6} lg={6} xl={4} item>
        <Skeleton animation="wave" variant="rectangular" height={200} />
      </Grid>
    </Grid>
  );
};
